import api from './client';
import { Timestamp } from 'firebase/firestore';
import type { Project } from 'types';
import type { ListParams } from './types';

// --- API Response types (ISO date strings from backend) ---

interface ProjectApiResponse {
  id: string;
  infos: any;
  org_id: string;
  author_id: string;
  status: string;
  history_events: any[];
  themes: any[];
  photos: any[];
  folders: any[];
}

// --- Date conversion helpers ---

/**
 * Convert an ISO date string or datetime from API to a Firebase Timestamp.
 * Returns Timestamp.now() if the value is falsy.
 */
function toTimestamp(value: any): Timestamp {
  if (!value) return Timestamp.now();
  if (value instanceof Timestamp) return value;
  if (typeof value === 'string') {
    return Timestamp.fromDate(new Date(value));
  }
  // Handle Firestore-like {seconds, nanoseconds} objects
  if (value.seconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  return Timestamp.now();
}

/**
 * Convert a Firebase Timestamp (or any date-like value) to an ISO string for sending to the API.
 * Handles proper Timestamps, plain {seconds, nanoseconds} objects, and Date objects defensively.
 */
function fromTimestamp(ts: any): string {
  if (!ts) return new Date().toISOString();
  if (ts instanceof Timestamp) return ts.toDate().toISOString();
  if (ts instanceof Date) return ts.toISOString();
  if (typeof ts === 'string') return ts;
  if (typeof ts === 'object' && ts.seconds !== undefined) {
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6).toISOString();
  }
  return new Date().toISOString();
}

/**
 * Convert an API project response to a frontend Project type,
 * converting all ISO date strings to Firebase Timestamps.
 */
function apiResponseToProject(response: ProjectApiResponse): Project {
  return {
    ...response,
    infos: {
      ...response.infos,
      general: {
        ...response.infos.general,
        creation_date: toTimestamp(response.infos.general?.creation_date),
        last_updated: toTimestamp(response.infos.general?.last_updated),
      },
      client: {
        ...response.infos.client,
        contacts: response.infos.client?.contacts || [],
        last_activity_date: response.infos.client?.last_activity_date
          ? toTimestamp(response.infos.client.last_activity_date)
          : undefined,
      },
      team: response.infos.team || {},
      partners: response.infos.partners || [],
    },
    history_events: (response.history_events || []).map((event: any) => ({
      ...event,
      date: toTimestamp(event.date),
      control_point: event.control_point || undefined,
      email: event.email
        ? {
            ...event.email,
          }
        : undefined,
    })),
    themes: response.themes || [],
    photos: response.photos || [],
    folders: response.folders || [],
  };
}

/**
 * Convert a frontend Project to API-compatible format,
 * converting Firebase Timestamps to ISO strings.
 */
function projectToApiPayload(project: Project): any {
  return {
    ...project,
    infos: {
      ...project.infos,
      general: {
        ...project.infos.general,
        creation_date: fromTimestamp(project.infos.general.creation_date),
        last_updated: fromTimestamp(project.infos.general.last_updated),
      },
      client: {
        ...project.infos.client,
        last_activity_date: project.infos.client.last_activity_date
          ? fromTimestamp(project.infos.client.last_activity_date)
          : undefined,
      },
    },
    themes: (project.themes || []).map((theme) => ({
      ...theme,
      steps: (theme.steps || []).map((step) => ({
        ...step,
        controlpoints: (step.controlpoints || []).map((cp) => ({
          ...cp,
          email: cp.email
            ? {
                ...cp.email,
                date: cp.email.date ? fromTimestamp(cp.email.date) : undefined,
              }
            : undefined,
        })),
      })),
    })),
    history_events: (project.history_events || []).map((event) => ({
      ...event,
      date: fromTimestamp(event.date),
    })),
  };
}

// --- API service ---

export const projectsApi = {
  /**
   * List all projects, optionally filtered by org and status
   */
  list: async (params?: ListParams & { org_id?: string; status?: string; offset?: number; sort_by?: string; sort_dir?: string }): Promise<{ items: Project[]; total: number }> => {
    const resp = await api.get('/projects', { params });
    return { items: (resp.data as ProjectApiResponse[]).map(apiResponseToProject), total: parseInt(resp.headers['x-total-count'] || '0', 10) };
  },

  /**
   * Get a specific project by ID
   */
  get: async (projectId: string): Promise<Project> => {
    const { data } = await api.get(`/projects/${projectId}`);
    return apiResponseToProject(data);
  },

  /**
   * Create a new project
   */
  create: async (project: {
    name: string;
    num?: string;
    org_id: string;
    status?: string;
    address?: string;
    city?: string;
    postal?: string;
    country?: string;
    notes?: string;
    themes?: any[];
    folders?: any[];
    contacts?: any[];
    partners?: any[];
    team?: any;
  }): Promise<Project> => {
    const { data } = await api.post('/projects', project);
    return apiResponseToProject(data);
  },

  /**
   * Update an existing project.
   * Can send individual fields or a full project payload.
   */
  update: async (projectId: string, updates: any): Promise<Project> => {
    const { data } = await api.patch(`/projects/${projectId}`, updates);
    return apiResponseToProject(data);
  },

  /**
   * Save a full project (converts Timestamps and sends the whole structure).
   * Use this when the frontend has made local changes to the full project object.
   */
  save: async (project: Project): Promise<Project> => {
    const payload = projectToApiPayload(project);
    const { data } = await api.patch(`/projects/${project.id}`, {
      infos: payload.infos,
      themes: payload.themes,
      folders: payload.folders,
      photos: payload.photos,
      status: payload.status,
    });
    return apiResponseToProject(data);
  },

  /**
   * Delete a project
   */
  delete: async (projectId: string): Promise<void> => {
    await api.delete(`/projects/${projectId}`);
  },

  /**
   * Upload files (documents or photos) to a project's GCS storage.
   * Returns array of {id, url, name, content_type, size} for each uploaded file.
   */
  uploadFiles: async (projectId: string, files: File[]): Promise<UploadedFile[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const { data } = await api.post(`/projects/${projectId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  addPhotos: async (
    projectId: string,
    files: File[],
    controlpointId?: string,
    hidden?: boolean,
  ): Promise<Project> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (controlpointId) formData.append('controlpoint_id', controlpointId);
    if (hidden !== undefined) formData.append('hidden', String(hidden));
    const { data } = await api.post(`/projects/${projectId}/add-photos`, formData);
    return apiResponseToProject(data);
  },

  addDocuments: async (
    projectId: string,
    files: File[],
    folderId: string,
    subfolderId?: string,
    hidden?: boolean,
  ): Promise<Project> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('folder_id', folderId);
    if (subfolderId) formData.append('subfolder_id', subfolderId);
    if (hidden !== undefined) formData.append('hidden', String(hidden));
    const { data } = await api.post(`/projects/${projectId}/add-documents`, formData);
    return apiResponseToProject(data);
  },

  sendEmailWithHistory: async (
    projectId: string,
    emailData: {
      id?: string;
      subject: string;
      body: string;
      org_id: string;
      project_id: string;
      sender_user_id: string;
      email_template_id?: string;
      emails_to?: string[];
      emails_cc?: string[];
      emails_bcc?: string[];
      files: any[];
      photos_id?: string[];
      controlpoint_id?: string;
    }
  ): Promise<Project> => {
    const { data } = await api.post(`/projects/${projectId}/send-email`, emailData);
    return apiResponseToProject(data);
  },

  sendAccessEmail: async (projectId: string, userId: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/projects/${projectId}/send-access-email/${userId}`);
    return data;
  },

  updateControlpointState: async (
    projectId: string,
    controlpointId: string,
    newState: string
  ): Promise<Project> => {
    const { data } = await api.patch(`/projects/${projectId}/controlpoint`, {
      controlpoint_id: controlpointId,
      new_state: newState,
    });
    return apiResponseToProject(data);
  },
};

export interface UploadedFile {
  id: string;
  url: string;
  name: string;
  content_type: string;
  size: number;
}
